// Shared attachment state + upload/delete logic, extracted from
// AttachmentsSection so the project-detail page can split the *list* and the
// *drop-zone* into separate cards while sharing one source of truth (items,
// pending uploads, handlers). AttachmentsSection (task case) reuses this hook
// too, so the logic lives in exactly one place.

import { useEffect, useRef, useState } from "preact/hooks";
import type { AttachmentMeta } from "@juuplan/shared";
import {
  deleteAttachment,
  fetchAttachments,
  fetchProjectAttachments,
  fetchPersonalTaskAttachments,
  uploadAttachment,
  uploadProjectAttachment,
  uploadPersonalTaskAttachment,
} from "../../store/project";
import { toastError, toastSuccess } from "../../lib/toast";
import { confirmDialog } from "../../lib/confirm";

// Typen, die der Server inline rendern kann (= /view-Endpoint). Muss zur
// serverseitigen INLINE_VIEWABLE-Liste passen (Bilder ohne SVG + PDF).
export const PREVIEWABLE = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
]);

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// MIME-types the server accepts (must mirror ALLOWED_PREFIXES/ALLOWED_EXACT
// in apps/server/src/routes/attachments.ts, or the client rejects files the
// server would actually accept).
const ALLOWED_MIME_PREFIXES = [
  "image/",
  "application/vnd.openxmlformats-officedocument.", // .docx/.xlsx/.pptx/...
];
const ALLOWED_MIME_EXACT = new Set<string>([
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/msword", // .doc
]);

function isAllowedMime(t: string): boolean {
  if (ALLOWED_MIME_EXACT.has(t)) return true;
  return ALLOWED_MIME_PREFIXES.some((p) => t.startsWith(p));
}

export function prettyBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "gerade eben";
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `vor ${hr} Std.`;
  const days = Math.round(hr / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  return new Date(iso).toLocaleDateString("de-DE");
}

export interface PendingUpload {
  /** Local id used only for the list key. */
  localId: number;
  filename: string;
  size: number;
}

let pendingCounter = 1;

export interface UseAttachments {
  items: AttachmentMeta[];
  pending: PendingUpload[];
  loading: boolean;
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  totalCount: number;
  fileInputRef: { current: HTMLInputElement | null };
  /** Open the native file picker. */
  openPicker: () => void;
  /** Accept a FileList/array (drop or picker) and upload each valid file. */
  handleFiles: (files: FileList | File[]) => void;
  /** Drop handler — preventDefault + extract files. */
  onDrop: (e: DragEvent) => void;
  /** Delete with confirm(). Returns when the row is removed. */
  doDelete: (a: AttachmentMeta) => Promise<void>;
}

/**
 * Owns attachment state for one target (task, project OR personal task).
 * Exactly one of taskId / projectId / personalTaskId should be set.
 */
export function useAttachments(target: {
  taskId?: number;
  projectId?: number;
  personalTaskId?: number;
}): UseAttachments {
  const { taskId, projectId, personalTaskId } = target;
  const [items, setItems] = useState<AttachmentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const promise =
      taskId !== undefined
        ? fetchAttachments(taskId)
        : projectId !== undefined
          ? fetchProjectAttachments(projectId)
          : personalTaskId !== undefined
            ? fetchPersonalTaskAttachments(personalTaskId)
            : Promise.resolve([]);
    void promise.then(
      (rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [taskId, projectId, personalTaskId]);

  const uploadOne = async (file: File): Promise<void> => {
    if (file.size > MAX_BYTES) {
      toastError(
        `Datei zu groß: ${file.name} (${prettyBytes(file.size)}). Maximal 10 MB.`
      );
      return;
    }
    if (!isAllowedMime(file.type)) {
      toastError(
        `Dateityp nicht erlaubt: ${file.name} (${file.type || "unbekannt"}).`
      );
      return;
    }
    const localId = pendingCounter++;
    setPending((p) => [...p, { localId, filename: file.name, size: file.size }]);
    try {
      const meta =
        taskId !== undefined
          ? await uploadAttachment(taskId, file)
          : projectId !== undefined
            ? await uploadProjectAttachment(projectId, file)
            : await uploadPersonalTaskAttachment(personalTaskId!, file);
      setItems((prev) => [...prev, meta]);
      toastSuccess(`${file.name} hochgeladen.`);
    } catch {
      // toast already shown in upload helper
    } finally {
      setPending((p) => p.filter((x) => x.localId !== localId));
    }
  };

  const handleFiles = (files: FileList | File[]): void => {
    const arr = Array.from(files);
    for (const f of arr) void uploadOne(f);
  };

  const openPicker = (): void => {
    fileInputRef.current?.click();
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    if (dt.files && dt.files.length > 0) handleFiles(dt.files);
  };

  const doDelete = async (a: AttachmentMeta): Promise<void> => {
    const ok = await confirmDialog({
      title: "Anhang löschen",
      message: `„${a.filename}" wirklich löschen?`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteAttachment(a.id, taskId);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch {
      // toast surfaced by api wrapper
    }
  };

  return {
    items,
    pending,
    loading,
    dragOver,
    setDragOver,
    totalCount: items.length + pending.length,
    fileInputRef,
    openPicker,
    handleFiles,
    onDrop,
    doDelete,
  };
}
